import React, { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './header-notification-banner.module.less';
import { messages } from './messages';
import { PhoneVerificationEnrollment } from './phone-verification-enrollment';
import { PHONE_VERIFICATION_STATES } from '../../authentication/constants';
import { UserSettings } from '../../enums/user-settings/user-settings';
import { StepUpAuthContextProvider } from '../../global/step-up-authentication';
import { trackPasswordChallenge, trackSuccess, USER_ACTIONS } from '../../global/step-up-authentication/analytics-helpers';
import { messageSets } from '../../global/step-up-authentication/messages';
import { useModal } from '../../hooks/use-modal';
import { useHasVerifiedPhone } from '../../hooks/use-phone-verification-status';
import { saveUserSetting } from '../../store/user/user.actions';

const Phases = {
  IDLE: 'IDLE',
  CHALLENGING: 'CHALLENGING',
  AUTHENTICATED: 'AUTHENTICATED',
  ENROLLING: 'ENROLLING',
};

const featureName = USER_ACTIONS.MFA_SETTINGS;

export const PhoneVerificationBanner = () => {
  const { isOpen, open, close } = useModal();
  const [phase, setPhase] = useAsyncSafeState(Phases.IDLE);
  const dispatch = useDispatch();
  const { verifiedPhoneState } = useHasVerifiedPhone();

  // Auto-start auth for modal or unskippable states
  useEffect(() => {
    if (verifiedPhoneState === PHONE_VERIFICATION_STATES.MODAL || verifiedPhoneState === PHONE_VERIFICATION_STATES.UNSKIPPABLE) {
      setPhase(Phases.CHALLENGING);
      open();
      trackPasswordChallenge({ source: featureName });
    }
  }, [verifiedPhoneState, setPhase, open]);

  const onModalDismissed = useCallback(() => {
    // Only allow dismissal if not in unskippable state
    if (verifiedPhoneState !== PHONE_VERIFICATION_STATES.UNSKIPPABLE) {
      dispatch(
        saveUserSetting({
          settingName: UserSettings.userHasVerifiedPhoneNumber.settingName,
          settingValue: `${verifiedPhoneState}_dismissed-${new Date().toISOString().split('T')[0]}`,
          excludeV3prefix: true,
        }),
      );
    }
  }, [dispatch, verifiedPhoneState]);

  const startStepUpAuth = useCallback(() => {
    setPhase(Phases.CHALLENGING);
    open();
    trackPasswordChallenge({ source: featureName });
  }, [setPhase, open]);

  const onCloseModal = useCallback(() => {
    close();
    onModalDismissed();
    setPhase(Phases.IDLE);
  }, [close, onModalDismissed, setPhase]);

  const onStepUpAuthSuccess = useCallback(() => {
    setPhase(Phases.ENROLLING);
    trackSuccess({ source: featureName });
  }, [setPhase]);

  const onEnrollmentClose = useCallback(() => {
    close();
    onModalDismissed();
    setPhase(Phases.IDLE);
  }, [close, onModalDismissed, setPhase]);

  const onEnrollmentSuccess = useCallback(() => {
    close();
    setPhase(Phases.IDLE);
  }, [close, setPhase]);
  return (
    <>
      <BannerMessageV2
        buttons={
          <Button onClick={startStepUpAuth} theme='link'>
            <FormattedMessage {...messages.phoneVerificationButton} />
          </Button>
        }
        className={style['banner-padding']}
        content={<Text textType={TextType.Paragraph} value={messages.phoneVerificationContent} />}
        layout={BannerLayout.Default}
        status={BannerStatus.Warning}
      />
      {(phase === Phases.CHALLENGING || phase === Phases.ENROLLING) && (
        <StepUpAuthContextProvider
          isMfaSettings
          isModalOpen={isOpen}
          isUnskippable={verifiedPhoneState === PHONE_VERIFICATION_STATES.UNSKIPPABLE}
          messages={messageSets.mfaSettingsMessages}
          onClose={onCloseModal}
          onStepUpAuthSuccess={onStepUpAuthSuccess}
          phoneVerificationState={verifiedPhoneState}
          source={featureName}
          stepUpAuthModalTitle={messages.phoneVerificationButton}
        >
          {phase === Phases.ENROLLING && (
            <PhoneVerificationEnrollment
              isOpen={phase === Phases.ENROLLING}
              isUnskippable={verifiedPhoneState === PHONE_VERIFICATION_STATES.UNSKIPPABLE}
              onClose={onEnrollmentClose}
              onSuccess={onEnrollmentSuccess}
              phoneVerificationState={verifiedPhoneState}
            />
          )}
        </StepUpAuthContextProvider>
      )}
    </>
  );
};
